'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import { Token, token } from '@flipster/styled-system/tokens';
import Num from '@flipster/utils/num';

export interface AllocationChartItem {
  key: string;
  /** 0~1 비율값 (e.g. "0.35") */
  percentage: string;
  tooltipContent: ReactNode;
}

interface AllocationChartProps {
  items: AllocationChartItem[];
  emptyMessage?: ReactNode;
}

export const AllocationChart = ({ items, emptyMessage }: AllocationChartProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const chartItems = useMemo(() => {
    let acc = Num(0);
    return items.map((item, idx) => {
      const start = acc;
      const percentage = Num(item.percentage);
      const color = idx < ALLOCATION_CHART_COLORS.length ? ALLOCATION_CHART_COLORS[idx] : ALLOCATION_CHART_OTHERS_COLOR;
      acc = acc.plus(percentage);
      return { ...item, start, percentage, color };
    });
  }, [items]);

  const isEmpty = items.length === 0;
  const diameter = 200;
  const strokeWidth = 32;
  const radius = (diameter - strokeWidth) / 2;

  const innerRadius = radius - strokeWidth / 2 - 4;
  const c = 2 * Math.PI * radius;
  const eps = chartItems.length > 1 ? 0.004 : 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (hoveredIndex === null || !svgRef.current) {
        return;
      }

      const target = event.target as Node;
      if (svgRef.current.contains(target)) {
        return;
      }

      const svgRect = svgRef.current.getBoundingClientRect();
      const clientX = 'touches' in event ? (event.touches[0]?.clientX ?? 0) : event.clientX;
      const clientY = 'touches' in event ? (event.touches[0]?.clientY ?? 0) : event.clientY;

      const x = ((clientX - svgRect.left) / svgRect.width) * diameter;
      const y = ((clientY - svgRect.top) / svgRect.height) * diameter;

      const centerX = diameter / 2;
      const centerY = diameter / 2;
      const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));

      const outerRadius = radius + strokeWidth / 2;
      if (distance > outerRadius) {
        setHoveredIndex(null);
      }
    };

    if (hoveredIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [hoveredIndex, radius, diameter, strokeWidth]);

  const hoveredItem = hoveredIndex !== null ? chartItems[hoveredIndex] : null;

  return (
    <div className={center({ position: 'relative', tablet: { px: 94 } })}>
      <svg
        ref={svgRef}
        className={css({
          w: diameter,
          h: diameter,
        })}
        viewBox={`0 0 ${diameter} ${diameter}`}
      >
        {isEmpty ? (
          <circle
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
            fill='transparent'
            stroke={token('colors.background.default.level-1')}
            strokeWidth={strokeWidth}
          />
        ) : (
          <>
            {chartItems.map((item, idx) => {
              const p = item.percentage.toNumber();
              const gapEps = p >= eps ? eps : 0;
              const dashLength = (p - gapEps) * c;
              const gapLength = c - dashLength;
              return (
                <circle
                  key={`${item.key}-${idx}`}
                  cx={diameter / 2}
                  cy={diameter / 2}
                  r={radius}
                  fill='transparent'
                  stroke={token(item.color)}
                  strokeWidth={strokeWidth}
                  strokeDashoffset={`${(0.25 - item.start.toNumber()) * c}`}
                  strokeDasharray={`${dashLength} ${gapLength}`}
                  className={css({
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'stroke',
                    cursor: 'pointer',
                    opacity: hoveredIndex !== null && hoveredIndex !== idx ? 0.5 : 1,
                  })}
                  onPointerEnter={() => setHoveredIndex(idx)}
                  onPointerLeave={() => setHoveredIndex(null)}
                  onClick={() => setHoveredIndex(idx)}
                />
              );
            })}
          </>
        )}

        <circle cx={diameter / 2} cy={diameter / 2} r={innerRadius} fill={token('colors.background.default.level-0')} />
      </svg>

      {isEmpty && emptyMessage && (
        <div
          className={css({
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          })}
        >
          <p
            className={css({
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-3',
            })}
          >
            {emptyMessage}
          </p>
        </div>
      )}

      {!isEmpty && hoveredItem && (
        <div
          className={css({
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease',
            opacity: 1,
          })}
        >
          <div
            className={css({
              px: 12,
              py: 8,
              rounded: 8,
              bg: 'background.default.level-1',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-1',
            })}
          >
            <p
              className={css({
                textStyle: 'Text/S/Regular',
                color: 'content.default.level-5',
                whiteSpace: 'nowrap',
              })}
            >
              {hoveredItem.tooltipContent}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const ALLOCATION_CHART_COLORS: Token[] = [
  'colors.blue.500',
  'colors.green.600',
  'colors.yellow.700',
  'colors.indigo.600',
  'colors.red.600',
  'colors.red.400',
  'colors.blue.800',
  'colors.indigo.400',
  'colors.indigo.800',
  'colors.blue.400',
  'colors.blue.300',
  'colors.blue.600',
  'colors.green.500',
  'colors.green.400',
  'colors.green.700',
  'colors.green.300',
  'colors.yellow.600',
  'colors.yellow.500',
  'colors.yellow.400',
  'colors.yellow.300',
  'colors.indigo.500',
  'colors.indigo.700',
  'colors.indigo.300',
  'colors.red.500',
  'colors.red.300',
  'colors.red.700',
  'colors.red.800',
  'colors.gray.500',
  'colors.gray.100',
  'colors.gray.50',
  'colors.gray.300',
];

export const ALLOCATION_CHART_OTHERS_COLOR: Token = 'colors.gray.300';
